import { eventBus } from '@/infrastructure/eventBus';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * Cross-tab bridge for SecureStorage change events.
 *
 * `SecureStorageWebCrypto` persists writes into IndexedDB (which is shared
 * across tabs of the same origin) but signals consumers through the in-memory
 * `eventBus`, which is per-tab. Without a bridge, tab B keeps rendering stale
 * state after tab A toggles a save/like even though the underlying data is
 * already updated.
 *
 * This module re-publishes every secure-storage write/remove onto a
 * `BroadcastChannel` (one channel per browser origin) and, on the receiving
 * side, re-emits the event into the local `eventBus` so existing
 * `eventBus.on` / `eventBus.onPrefix` listeners pick it up with zero
 * consumer-side changes.
 *
 * **Echo suppression.** Each tab tags outbound messages with a per-tab
 * random id. Incoming messages tagged with our own id are ignored so the
 * sender never re-emits its own write (the local `eventBus.emit` in
 * `SecureStorageWebCrypto.setEncrypted`/`remove` already handled the
 * same-tab path before this bridge ran).
 *
 * **Fallback.** Environments without `BroadcastChannel` (older browsers,
 * some test runtimes — `happy-dom` does not implement it as of this
 * writing) fall back to a `localStorage` + `StorageEvent` transport: we
 * write a JSON payload under a sentinel key and immediately remove it.
 * `storage` events fire only on OTHER tabs (not the originator), giving
 * us echo suppression for free; we keep the per-tab id anyway as belt
 * and braces.
 *
 * **Payload constraint.** Values published to the bridge MUST be
 * JSON-serialisable. All current secure-storage payloads (`SavedUserStorage`,
 * `SavedContentStorage`, `UserMediaLikeStorage`, etc.) are plain objects
 * of strings/numbers/booleans/dates-as-ISO-strings, so this holds; callers
 * adding new payload shapes should verify the round-trip.
 */

const BROADCAST_CHANNEL_NAME = 'expause-secure-storage';
const FALLBACK_STORAGE_KEY = '__expause_secure_storage_event__';

interface CrossTabMessage {
  /**
   * Random id identifying the publishing tab. Receivers compare against
   * their own `tabId` and ignore messages they sent themselves.
   */
  tabId: string;
  /** The full storage key the write targeted (e.g. `'SavedUser_abc'`). */
  key: string;
  /** The new value, or `null` for a `remove`. JSON-serialisable. */
  value: unknown;
}

// Per-tab id — generated once at module load. `crypto.randomUUID()` is
// universally available in modern browsers; fall back to a small random
// string for environments that lack it (older Safari, some test runtimes).
const TAB_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;

let channel: BroadcastChannel | null = null;
let fallbackStorageHandler: ((event: StorageEvent) => void) | null = null;

/**
 * Re-emit a remote write/remove onto the local `eventBus`, mirroring the
 * shape `SecureStorageWebCrypto.setEncrypted`/`remove` already use.
 */
function dispatchLocally(message: CrossTabMessage): void {
  if (message.tabId === TAB_ID) {
    return; // Ignore our own echoes.
  }

  try {
    eventBus.emit(message.key, { value: message.value });
  } catch (error) {
    appLogger.error(
      'Failed to dispatch cross-tab storage event locally',
      'secureStorageEventBridge.dispatchLocally',
      { error: error as Error },
    );
  }
}

/**
 * Initialise the cross-tab bridge. Idempotent — calling it more than
 * once is a no-op. Called automatically the first time
 * `publishStorageChange` runs, but can be invoked eagerly (e.g. on app
 * boot) to start receiving events before the first local write.
 */
export function initialiseSecureStorageBridge(): void {
  if (channel !== null || fallbackStorageHandler !== null) {
    return;
  }

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as CrossTabMessage | undefined;
        if (!data || typeof data.key !== 'string') {
          return;
        }
        dispatchLocally(data);
      });
      return;
    } catch (error) {
      appLogger.error(
        'BroadcastChannel construction failed; falling back to localStorage transport',
        'secureStorageEventBridge.initialiseSecureStorageBridge',
        { error: error as Error },
      );
      channel = null;
    }
  }

  // Fallback transport: localStorage `StorageEvent`s only fire on OTHER
  // tabs, so the same-tab echo problem is already handled by the browser.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    fallbackStorageHandler = (event: StorageEvent) => {
      if (event.key !== FALLBACK_STORAGE_KEY || event.newValue === null) {
        return;
      }
      try {
        const parsed = JSON.parse(event.newValue) as CrossTabMessage;
        if (!parsed || typeof parsed.key !== 'string') {
          return;
        }
        dispatchLocally(parsed);
      } catch (error) {
        appLogger.error(
          'Failed to parse cross-tab storage event payload',
          'secureStorageEventBridge.initialiseSecureStorageBridge',
          { error: error as Error },
        );
      }
    };
    window.addEventListener('storage', fallbackStorageHandler);
  }
}

/**
 * Publish a secure-storage write or remove to other tabs. Same-tab
 * listeners are NOT notified here — the caller (`SecureStorageWebCrypto`)
 * has already invoked `eventBus.emit` for the local case.
 *
 * Lazily initialises the bridge on first call. Failures are logged but
 * never propagated: a broken cross-tab transport must not break the
 * write path.
 *
 * @param key   Full storage key (e.g. `'SavedUser_abc'`). Must match
 *              the key the local `eventBus.emit` used.
 * @param value The persisted value, or `null` for a `remove`. Must be
 *              JSON-serialisable.
 */
export function publishStorageChange(key: string, value: unknown): void {
  initialiseSecureStorageBridge();

  const message: CrossTabMessage = { tabId: TAB_ID, key, value };

  if (channel !== null) {
    try {
      channel.postMessage(message);
      return;
    } catch (error) {
      appLogger.error(
        'BroadcastChannel postMessage failed',
        'secureStorageEventBridge.publishStorageChange',
        { error: error as Error },
      );
    }
  }

  // Fallback path — write+remove the sentinel localStorage key. The
  // remove keeps the key clean and also fires a second `storage` event
  // on listeners, which we ignore (newValue === null).
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(message));
      window.localStorage.removeItem(FALLBACK_STORAGE_KEY);
    } catch (error) {
      appLogger.error(
        'localStorage fallback transport failed',
        'secureStorageEventBridge.publishStorageChange',
        { error: error as Error },
      );
    }
  }
}

/**
 * Tear down the bridge. Intended for test isolation — production code
 * keeps the bridge alive for the lifetime of the tab.
 */
export function disposeSecureStorageBridge(): void {
  if (channel !== null) {
    try {
      channel.close();
    } catch {
      // ignore — channel was already torn down
    }
    channel = null;
  }
  if (fallbackStorageHandler !== null && typeof window !== 'undefined') {
    window.removeEventListener('storage', fallbackStorageHandler);
    fallbackStorageHandler = null;
  }
}

/**
 * Expose the per-tab id for tests that need to simulate echo
 * suppression. Not intended for production callers.
 */
export function __getTabIdForTests(): string {
  return TAB_ID;
}
